// A drag with a pointer. The element reports how far it moved from the place
// it had when the drag started. The distance stops at the edge of the window,
// so the element cannot leave the screen.

export type DragOffset = { x: number; y: number };

export type DragOptions = {
  /** False stops a drag before it starts. */
  canDrag: () => boolean;
  /** The offset the element has now, before the drag adds to it. */
  offset: () => DragOffset;
  onMove: (offset: DragOffset) => void;
  /** The room to keep between the element and the edge of the window, in pixels. */
  margin: number;
  draggingClass: string;
};

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), Math.max(low, high));

export const makeDraggable = (node: HTMLElement, options: DragOptions): void => {
  node.addEventListener('pointerdown', (down) => {
    if (down.button !== 0 || !options.canDrag()) return;
    down.preventDefault();
    const start = options.offset();
    const box = node.getBoundingClientRect();
    const { margin } = options;

    const move = (event: PointerEvent): void => {
      const dx = clamp(
        event.clientX - down.clientX,
        margin - box.left,
        window.innerWidth - margin - box.right,
      );
      const dy = clamp(
        event.clientY - down.clientY,
        margin - box.top,
        window.innerHeight - margin - box.bottom,
      );
      options.onMove({ x: start.x + dx, y: start.y + dy });
    };

    const end = (): void => {
      node.classList.remove(options.draggingClass);
      node.removeEventListener('pointermove', move);
      node.removeEventListener('pointerup', end);
      node.removeEventListener('pointercancel', end);
    };

    node.setPointerCapture(down.pointerId);
    node.classList.add(options.draggingClass);
    node.addEventListener('pointermove', move);
    node.addEventListener('pointerup', end);
    node.addEventListener('pointercancel', end);
  });
};
